import React, { useEffect, useMemo, useState, useRef } from "react";
import { useMutation } from "@apollo/client";
import BackButton from "../CommonComponents/BackButton";
import LabledInput from "../CommonComponents/LabledInput";
import CustomSelect from "../CommonComponents/CustomSelect";
import "../../index.css";

import {
  ADD_ADMIN,
  UPDATE_ADMIN,
  GET_ADMINS,
} from "../../graphql/userManagement";

/**
 * Props:
 * - onClose(fn)            : called when user cancels / goes back
 * - editData (object)      : when present, form is in edit mode
 * - onSaved (optional fn)  : callback invoked after successful save (receives admin object)
 *
 * NOTE: prefer onClose instead of setAddAdmin to avoid confusion between add/edit toggles.
 */
const AddAdminForm = ({ onClose, editData = {}, onSaved }) => {
  const isEditMode = useMemo(() => Boolean(editData?.id), [editData?.id]);

  // Form state
  const [fullName, setFullName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [assignedGender, setAssignedGender] = useState("");

  // track the id we seeded from, so we don't reseed when parent rerenders with same editData
  const seededIdRef = useRef(null);

  // Mutations
  const [addAdmin, { loading: addLoading }] = useMutation(ADD_ADMIN, {
    refetchQueries: [{ query: GET_ADMINS }],
    awaitRefetchQueries: true,
    update(cache, { data }) {
      const newAdmin = data?.addAdmin?.admin;
      if (!newAdmin) return;
      try {
        const existing = cache.readQuery({ query: GET_ADMINS });
        const prev = existing?.getAdminList?.admins || [];
        cache.writeQuery({
          query: GET_ADMINS,
          data: {
            getAdminList: {
              ...existing.getAdminList,
              admins: [newAdmin, ...prev],
            },
          },
        });
      } catch (err) {
        // refetchQueries will handle it if cache unavailable
      }
    },
  });

  const [updateAdmin, { loading: updateLoading }] = useMutation(UPDATE_ADMIN, {
    refetchQueries: [{ query: GET_ADMINS }],
    awaitRefetchQueries: true,
    update(cache, { data }) {
      const updated = data?.updateAdmin?.admin;
      if (!updated) return;
      try {
        const existing = cache.readQuery({ query: GET_ADMINS });
        const next = (existing?.getAdminList?.admins || []).map((a) =>
          a.id === updated.id ? updated : a
        );
        cache.writeQuery({
          query: GET_ADMINS,
          data: { getAdminList: { ...existing.getAdminList, admins: next } },
        });
      } catch (err) {
        // noop (refetchQueries will handle)
      }
    },
  });

  const loading = addLoading || updateLoading;

  // Seed form only when editData.id changes (or on mount)
  useEffect(() => {
    const incomingId = editData?.id || null;

    // If editing and we haven't seeded this particular record yet, seed fields
    if (incomingId && seededIdRef.current !== incomingId) {
      seededIdRef.current = incomingId;
      setFullName(editData.fullName || "");
      setContactNumber(editData.contactNumber || "");
      setAssignedGender(editData.gender || "");
      return;
    }

    // If switching from edit -> add mode (incomingId is null) and we previously seeded,
    // reset seededIdRef so future edits will seed appropriately.
    if (!incomingId) {
      seededIdRef.current = null;
    }

    // Only seed blank default for add mode once (do not keep resetting on every parent render)
    if (!incomingId && seededIdRef.current === null) {
      // mark seeded so we don't re-seed repeatedly
      seededIdRef.current = "EMPTY_SEEDED";
      setFullName("");
      setContactNumber("");
      setAssignedGender("");
    }
    // intentionally do not include other deps so this effect won't run while user types
  }, [
    editData?.id,
    editData?.fullName,
    editData?.contactNumber,
    editData?.gender,
  ]);

  const validate = () => {
    if (!fullName.trim()) return "Name is required.";
    if (!contactNumber.trim() && !isEditMode)
      return "Contact number is required.";
    if (!assignedGender) return "Please select a gender.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    try {
      if (isEditMode) {
        const payload = {
          id: editData.id,
          fullName: fullName.trim(),
          gender: assignedGender,
        };
        const res = await updateAdmin({ variables: { data: payload } });
        if (res?.data?.updateAdmin?.status === "SUCCESS") {
          if (typeof onSaved === "function")
            onSaved(res.data.updateAdmin.admin || null);
          if (typeof onClose === "function") onClose();
        } else {
          alert(res?.data?.updateAdmin?.message || "Update failed");
        }
      } else {
        const payload = {
          fullName: fullName.trim(),
          contactNumber: contactNumber.trim(),
          gender: assignedGender,
        };
        const res = await addAdmin({ variables: { data: payload } });
        if (res?.data?.addAdmin?.status === "SUCCESS") {
          if (typeof onSaved === "function")
            onSaved(res.data.addAdmin.admin || null);
          if (typeof onClose === "function") onClose();
        } else {
          alert(res?.data?.addAdmin?.message || "Creation failed");
        }
      }
    } catch (error) {
      console.error(error);
      alert("Something went wrong.");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col space-y-10">
      <BackButton
        onClick={() => (typeof onClose === "function" ? onClose() : null)}
      />

      <h3 className="font-be-jost-pro font-bold text-2xl text-[#FBB033]">
        {isEditMode ? "Edit Admin" : "Add Admin"}
      </h3>

      <LabledInput
        placeholder="Type name here*"
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
        label="Name"
        required
      />

      <LabledInput
        value={contactNumber}
        onChange={(e) => setContactNumber(e.target.value)}
        label="Contact"
        placeholder="Type contact number"
        specialLabel="+91"
        required={!isEditMode}
        disabled={isEditMode}
      />

      <CustomSelect
        label="Gender"
        placeholder="Select Gender"
        value={assignedGender}
        onChange={(e) => setAssignedGender(e.target.value)}
        options={["Male", "Female", "Other"]}
        required
        inline
        showReset
      />

      <div className="flex py-16 w-full justify-center items-center">
        <button
          type="submit"
          disabled={loading}
          className={`px-5 py-2 bg-[#FBB034] font-poppins text-sm font-bold rounded-md ${
            loading ? "opacity-70 cursor-not-allowed" : ""
          }`}
        >
          {loading
            ? isEditMode
              ? "Saving..."
              : "Adding..."
            : isEditMode
            ? "Save Changes"
            : "Add Admin"}
        </button>
      </div>
    </form>
  );
};

export default AddAdminForm;
